/**
 * Formats conversation payloads that llm-service stores as JSON into chat-friendly text.
 * Titles are unwrapped server-side; assistant bodies (name history, person search, etc.)
 * still arrive as structured JSON and must be rendered here.
 */

const CONVERSATION_DISPLAY_KEYS = [
  'prompt',
  'name',
  'main_person_name',
  'address',
  'search',
  'location',
] as const;

const PERSON_SEARCH_FIELD_LABELS: Record<string, string> = {
  first_name: 'First name',
  middle_name: 'Middle name',
  last_name: 'Last name',
  title: 'Title',
  company: 'Company',
  street: 'Street',
  unit: 'Unit',
  city: 'City',
  state: 'State',
  zipcode: 'Zip code',
  email: 'Email',
  phone: 'Phone',
  username: 'Username',
  vin: 'VIN',
  age: 'Age',
  date_of_death: 'Date of death',
};

const PERSON_SEARCH_KEYS = Object.keys(PERSON_SEARCH_FIELD_LABELS);

const EMPTY_MATCH_MESSAGE = "I couldn't find a match for that search.";

interface FamousPerson {
  headline?: string;
  blurb?: string;
}

interface SuggestedName {
  name?: string;
  blurb?: string;
  isSurname?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const tryParseJsonObject = (text: string): Record<string, unknown> | null => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const isTeaserPayload = (payload: Record<string, unknown>): boolean =>
  'permalink' in payload && 'data' in payload;

const isNameHistoryPayload = (payload: Record<string, unknown>): boolean =>
  typeof payload.name_meaning === 'string';

const isPersonSmartSearchPayload = (
  payload: Record<string, unknown>
): boolean =>
  'first_name' in payload &&
  'last_name' in payload &&
  PERSON_SEARCH_KEYS.some((key) => key in payload);

const isObituaryParserPayload = (payload: Record<string, unknown>): boolean =>
  typeof payload.main_person_data === 'string' ||
  typeof payload.main_person_name === 'string';

const nonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const formatBulletList = (items: unknown): string | null => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const lines = items
    .map((item) => nonEmptyString(item))
    .filter((item): item is string => item !== null)
    .map((item) => `• ${item}`);

  return lines.length > 0 ? lines.join('\n') : null;
};

const formatFamousPeople = (items: unknown): string | null => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const lines = items
    .map((item) => {
      if (!isRecord(item)) {
        return null;
      }
      const person = item as FamousPerson;
      const headline = nonEmptyString(person.headline);
      const blurb = nonEmptyString(person.blurb);
      if (headline && blurb) {
        return `• **${headline}** — ${blurb}`;
      }
      return headline ? `• **${headline}**` : blurb ? `• ${blurb}` : null;
    })
    .filter((line): line is string => line !== null);

  return lines.length > 0 ? lines.join('\n') : null;
};

const formatSuggestedNames = (items: unknown): string | null => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const lines = items
    .map((item) => {
      if (!isRecord(item)) {
        return null;
      }
      const suggestion = item as SuggestedName;
      const name = nonEmptyString(suggestion.name);
      if (!name) {
        return null;
      }
      const kind = suggestion.isSurname === true ? 'surname' : 'first name';
      const blurb = nonEmptyString(suggestion.blurb);
      return blurb
        ? `• **${name}** (${kind}) — ${blurb}`
        : `• **${name}** (${kind})`;
    })
    .filter((line): line is string => line !== null);

  return lines.length > 0 ? lines.join('\n') : null;
};

const formatNameHistoryPayload = (payload: Record<string, unknown>): string => {
  const sections: string[] = [];

  const nameMeaning = nonEmptyString(payload.name_meaning);
  if (nameMeaning) {
    sections.push(`**Name meaning**\n${nameMeaning}`);
  }

  const genderAssociation = nonEmptyString(payload.gender_association);
  if (genderAssociation) {
    sections.push(`**Gender association**\n${genderAssociation}`);
  }

  const globalUsage = nonEmptyString(payload.global_usage);
  if (globalUsage) {
    sections.push(`**Global usage**\n${globalUsage}`);
  }

  const famousPeople = formatFamousPeople(payload.famous_people);
  if (famousPeople) {
    sections.push(`**Famous people**\n${famousPeople}`);
  }

  const culturalFunFacts = formatBulletList(payload.cultural_fun_facts);
  if (culturalFunFacts) {
    sections.push(`**Cultural fun facts**\n${culturalFunFacts}`);
  }

  const historicalFunFacts = formatBulletList(payload.historical_fun_facts);
  if (historicalFunFacts) {
    sections.push(`**Historical fun facts**\n${historicalFunFacts}`);
  }

  const suggestedNames = formatSuggestedNames(payload.suggested_names);
  if (suggestedNames) {
    sections.push(`**Suggested names**\n${suggestedNames}`);
  }

  return sections.length > 0 ? sections.join('\n\n') : EMPTY_MATCH_MESSAGE;
};

const formatPersonSmartSearchPayload = (
  payload: Record<string, unknown>
): string => {
  const lines = PERSON_SEARCH_KEYS.map((key) => {
    const value = nonEmptyString(payload[key]);
    if (!value) {
      return null;
    }
    return `**${PERSON_SEARCH_FIELD_LABELS[key]}:** ${value}`;
  }).filter((line): line is string => line !== null);

  if (lines.length === 0) {
    return EMPTY_MATCH_MESSAGE;
  }

  const identityKeys = ['first_name', 'middle_name', 'last_name', 'title'];
  const hasIdentity = identityKeys.some(
    (key) => nonEmptyString(payload[key]) !== null
  );

  // Echo-only payloads (e.g. phone filled, person fields empty) are not useful as
  // a "result" — prefer the empty-match message and let Try Web Search handle it.
  if (!hasIdentity && lines.length <= 2) {
    return EMPTY_MATCH_MESSAGE;
  }

  return lines.join('\n');
};

const formatObituaryParserPayload = (
  payload: Record<string, unknown>
): string => {
  const sections: string[] = [];
  const name = nonEmptyString(payload.main_person_name);
  if (name) {
    sections.push(`**${name}**`);
  }

  const dateOfDeath = nonEmptyString(payload.main_person_date_of_death);
  if (dateOfDeath) {
    sections.push(`**Date of death:** ${dateOfDeath}`);
  }

  const dateOfBirth = nonEmptyString(payload.main_person_date_of_birth);
  if (dateOfBirth) {
    sections.push(`**Date of birth:** ${dateOfBirth}`);
  }

  const address = nonEmptyString(payload.main_person_address);
  if (address) {
    sections.push(`**Location:** ${address}`);
  }

  const data = nonEmptyString(payload.main_person_data);
  if (data) {
    sections.push(data);
  }

  return sections.length > 0 ? sections.join('\n\n') : EMPTY_MATCH_MESSAGE;
};

const unwrapConversationDisplayPayload = (
  payload: Record<string, unknown>
): string | null => {
  if (isTeaserPayload(payload)) {
    return null;
  }

  for (const key of CONVERSATION_DISPLAY_KEYS) {
    const value = nonEmptyString(payload[key]);
    if (value) {
      return value;
    }
  }

  const composedParts = ['first_name', 'last_name', 'title']
    .map((key) => nonEmptyString(payload[key]))
    .filter((value): value is string => value !== null);

  if (composedParts.length > 0) {
    return composedParts.join(' ');
  }

  return null;
};

/**
 * Turns stored conversation JSON into readable chat text when possible.
 * Returns null when the text is not a recognized structured payload.
 */
export const formatStructuredConversationPayload = (
  text: string
): string | null => {
  const payload = tryParseJsonObject(text);
  if (!payload) {
    return null;
  }

  if (isTeaserPayload(payload)) {
    return null;
  }

  if (isNameHistoryPayload(payload)) {
    return formatNameHistoryPayload(payload);
  }

  if (isPersonSmartSearchPayload(payload)) {
    return formatPersonSmartSearchPayload(payload);
  }

  if (isObituaryParserPayload(payload) && 'main_person_data' in payload) {
    return formatObituaryParserPayload(payload);
  }

  return unwrapConversationDisplayPayload(payload);
};
